import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';
import './common.css';
import './Dashboard.css';

function Dashboard() {
    const { user, logout, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');

    useEffect(() => {
        if (isAdmin()) {
            fetchUsers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await api.get('/api/users');
            if (response.data.success) {
                setUsers(response.data.data);
            }
        } catch (err) {
            setError('Failed to fetch users');
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleDeleteUser = async (userId, userName) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete user "${userName}"? This action cannot be undone.`
        );

        if (!confirmed) return;

        try {
            const response = await api.delete(`/api/users/${userId}`);
            if (response.data.success) {
                setSuccess('User deleted successfully');
                fetchUsers(); // Refresh the list
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to delete user');
            setTimeout(() => setError(''), 3000);
        }
    };

    const openPasswordModal = (u) => {
        setSelectedUser(u);
        setNewPassword('');
        setShowPasswordModal(true);
        setError('');
        setSuccess('');
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        try {
            // Admin can reset password directly (we'll need a new endpoint or use update)
            const response = await api.put(`/api/users/${selectedUser.id}/password`, {
                newPassword
            });

            if (response.data.success) {
                setSuccess('Password changed successfully');
                setShowPasswordModal(false);
                setNewPassword('');
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to change password');
        }
    };

    const handleToggleActive = async (userId, userName, isActive) => {
        const action = isActive ? 'deactivate' : 'activate';
        const confirmed = window.confirm(
            `Are you sure you want to ${action} user "${userName}"?`
        );

        if (!confirmed) return;

        try {
            const response = await api.put(`/api/users/${userId}/toggle-active`);
            if (response.data.success) {
                setSuccess(response.data.message);
                fetchUsers(); // Refresh the list
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            setError(err.response?.data?.error || `Failed to ${action} user`);
            setTimeout(() => setError(''), 3000);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="dashboard-container">
            <header className="dashboard-header">
                <h1>Dashboard</h1>
                <div className="user-info">
                    <span className="user-name">
                        {user?.firstName || user?.first_name} {user?.lastName || user?.last_name}
                    </span>
                    <span className={`user-badge ${user?.role}`}>
                        {user?.role}
                    </span>
                    <button className="btn btn-secondary" onClick={handleLogout}>
                        Logout
                    </button>
                </div>
            </header>

            <div className="dashboard-content">
                <div className="welcome-section">
                    <h2>Welcome, {user?.firstName || user?.first_name}!</h2>
                    <p>Email: {user?.email}</p>
                </div>

                <div className="quick-links">
                    <h3>Quick Links</h3>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate('/products')}
                    >
                        View Products
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate('/products/add')}
                    >
                        Add New Product
                    </button>
                </div>

                {isAdmin() && (
                    <div className="admin-section">
                        <div className="section-header">
                            <h3>User Management</h3>
                            <button
                                className="btn btn-primary"
                                onClick={() => navigate('/add-user')}
                            >
                                + Add New User
                            </button>
                        </div>

                        {error && <div className="error-message">{error}</div>}
                        {success && <div className="success-message">{success}</div>}

                        {loading ? (
                            <div className="loading">Loading users...</div>
                        ) : (
                            <div className="users-table-container">
                                <table className="users-table">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Role</th>
                                            <th>Status</th>
                                            <th>Created</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map((u) => (
                                            <tr key={u.id}>
                                                <td>{u.id}</td>
                                                <td>{u.firstName || u.first_name} {u.lastName || u.last_name}</td>
                                                <td>{u.email}</td>
                                                <td>
                                                    <span className={`role-badge ${u.role}`}>
                                                        {u.role}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`status-badge ${(u.isActive ?? u.is_active) ? 'active' : 'inactive'}`}>
                                                        {(u.isActive ?? u.is_active) ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td>{formatDate(u.createdAt || u.created_at)}</td>
                                                <td className="actions-cell">
                                                    <button
                                                        className={`btn btn-small ${(u.isActive ?? u.is_active) ? 'btn-warning' : 'btn-success'}`}
                                                        onClick={() => handleToggleActive(u.id, `${u.firstName || u.first_name} ${u.lastName || u.last_name}`, (u.isActive ?? u.is_active))}
                                                        disabled={u.id === user?.id}
                                                        title={(u.isActive ?? u.is_active) ? 'Deactivate User' : 'Activate User'}
                                                    >
                                                        {(u.isActive ?? u.is_active) ? '🚫 Deactivate' : '✅ Activate'}
                                                    </button>
                                                    <button
                                                        className="btn btn-small btn-password"
                                                        onClick={() => openPasswordModal(u)}
                                                        title="Change Password"
                                                    >
                                                        🔑 Password
                                                    </button>
                                                    <button
                                                        className="btn btn-small btn-danger"
                                                        onClick={() => handleDeleteUser(u.id, `${u.firstName || u.first_name} ${u.lastName || u.last_name}`)}
                                                        disabled={u.id === user?.id}
                                                        title={u.id === user?.id ? "Cannot delete yourself" : "Delete User"}
                                                    >
                                                        🗑️ Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {users.length === 0 && (
                                    <div className="no-users">
                                        No users found.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {!isAdmin() && (
                    <div className="user-section">
                        <p>Welcome to the Effective Arbitrage System. More features coming soon!</p>
                    </div>
                )}
            </div>

            {/* Password Change Modal */}
            {showPasswordModal && (
                <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Change Password</h3>
                        <p className="modal-subtitle">
                            User: {selectedUser?.firstName || selectedUser?.first_name} {selectedUser?.lastName || selectedUser?.last_name}
                        </p>

                        {error && <div className="error-message">{error}</div>}

                        <form onSubmit={handleChangePassword}>
                            <div className="form-group">
                                <label htmlFor="newPassword">New Password</label>
                                <input
                                    type="password"
                                    id="newPassword"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Minimum 6 characters"
                                    minLength="6"
                                    required
                                    autoFocus
                                />
                            </div>

                            <div className="modal-actions">
                                <button type="submit" className="btn btn-primary">
                                    Change Password
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowPasswordModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Dashboard;
